import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FileText, Store } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { formatINR } from "@/lib/format";
import { Field, TextInput, ErrorBox, btnPrimary, btnSecondary, inputCls } from "@/components/common/Form";
import { EmptyState, LoadingState } from "@/components/common/States";

export default function RequestQuote() {
  const { user } = useStore();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const slug = params.get("product");
  const [product, setProduct] = useState(null);
  const [form, setForm] = useState({ quantity: Number(params.get("qty")) || 1, note: "", delivery_city: "", delivery_state: "", delivery_pin: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!slug) return undefined;
    let cancelled = false;
    api.get(`/products/${slug}`).then((r) => {
      if (cancelled) return;
      setProduct(r.data.product);
      setForm((f) => ({ ...f, quantity: Math.max(f.quantity, r.data.product.minimum_order_quantity || 1) }));
    }).catch(() => { if (!cancelled) setProduct(false); });
    return () => { cancelled = true; };
  }, [slug]);

  if (user === null) return <LoadingState />;
  if (!user || user.role !== "business_buyer") {
    return <div className="max-w-xl mx-auto px-4 py-16"><EmptyState title="Business buyer account required" message="Wholesale quotations are available to registered business buyers. Sign in with a business account to continue." action={<Link to="/login" className={btnPrimary}>Sign in</Link>} /></div>;
  }
  if (!slug || product === false) {
    return <div className="max-w-xl mx-auto px-4 py-16"><EmptyState title="Choose a wholesale product" message="Open any product marked “Wholesale Available” and click Request Wholesale Quote." action={<Link to="/b2b/products" className={btnPrimary}>Browse wholesale products</Link>} /></div>;
  }
  if (!product) return <LoadingState label="Loading product..." />;
  if (!product.b2b_enabled) {
    return <div className="max-w-xl mx-auto px-4 py-16"><EmptyState title="Not available for wholesale" message={`${product.name} is not currently offered for wholesale purchase.`} action={<Link to={`/product/${product.slug}`} className={btnSecondary}>Back to product</Link>} /></div>;
  }

  const variant = params.get("variant") ? product.variants?.find((v) => v.id === params.get("variant")) : null;
  const moq = product.minimum_order_quantity || 1;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (Number(form.quantity) < moq) { setError(`Minimum order quantity is ${moq} units`); return; }
    setSaving(true);
    try {
      const r = await api.post("/business/rfqs", { items: [{ slug: product.slug, variant_id: variant?.id || null, quantity: Number(form.quantity) }], note: form.note, delivery_city: form.delivery_city, delivery_state: form.delivery_state, delivery_pin: form.delivery_pin });
      toast.success(`${r.data.rfq_number} submitted to ${r.data.vendor_name}`);
      navigate(`/business/rfqs/${r.data.id}?new=1`, { replace: true });
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="rfq-page">
      <div className="flex items-center gap-2 text-secondary mb-2"><FileText size={22} /><span className="eyebrow">B2B Wholesale</span></div>
      <h1 className="text-3xl font-serif font-bold text-stone-900">Request Wholesale Quote</h1>
      <p className="mt-2 text-stone-600">The vendor will review your requirement and respond with a quotation. No stock is reserved and no payment is taken at this stage.</p>

      <div className="mt-6 rounded-xl border border-border bg-card p-4 flex gap-4" data-testid="rfq-product">
        <img src={product.images?.[0]} alt="" className="h-20 w-20 rounded-lg object-cover bg-muted" />
        <div className="text-sm">
          <div className="font-semibold text-stone-900">{product.name}{variant && ` · ${variant.variant_name}`}</div>
          <div className="text-stone-500 inline-flex items-center gap-1 mt-0.5"><Store size={13} /> Vendor: <b className="text-stone-800" data-testid="rfq-vendor">{product.vendor_name}</b></div>
          <div className="mt-1 text-stone-600">MOQ <b data-testid="rfq-moq">{moq}</b> units{product.wholesale_unit_price != null && <> · Listed wholesale price <b>{formatINR(product.wholesale_unit_price)}</b> / unit</>}</div>
        </div>
      </div>

      <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="rfq-form">
        <div className="sm:col-span-2"><ErrorBox message={error} testId="rfq-error" /></div>
        <Field label={`Quantity (min ${moq}) *`}><TextInput testId="rfq-quantity" type="number" min={moq} required value={form.quantity} onChange={set("quantity")} /></Field>
        <Field label="Delivery PIN code *"><TextInput testId="rfq-pin" required value={form.delivery_pin} onChange={set("delivery_pin")} /></Field>
        <Field label="Delivery city *"><TextInput testId="rfq-city" required value={form.delivery_city} onChange={set("delivery_city")} /></Field>
        <Field label="Delivery state *"><TextInput testId="rfq-state" required value={form.delivery_state} onChange={set("delivery_state")} /></Field>
        <Field label="Requirement / note" className="sm:col-span-2"><textarea rows={4} data-testid="rfq-note" value={form.note} onChange={set("note")} className={inputCls} placeholder="Packaging, delivery timeline, customisation..." /></Field>
        <div className="sm:col-span-2 flex flex-wrap gap-2">
          <button disabled={saving} data-testid="rfq-submit-button" className={btnPrimary}>{saving ? "Submitting..." : "Submit RFQ"}</button>
          <Link to={`/product/${product.slug}`} className={btnSecondary}>Cancel</Link>
        </div>
      </form>
    </div>
  );
}
